import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Boxes, CheckCircle2, CircleDashed, Clock, KeyRound, ListChecks, Package, RadioTower, Receipt, ShieldCheck, TrendingDown, Users, XCircle } from 'lucide-react';
import type { ComponentType } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchRuntimeConfig } from '@/lib/api';
import { fetchStanVendingPlus, type StanVendingPlus } from '@/lib/panel-tokens-api';
import { fetchDetected } from '@/lib/alerts-api';
import { fetchOverview, type Overview } from '@/lib/overview-api';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { canSee, navItemFor } from '@/lib/nav';
import { cn } from '@/lib/utils';

const nf = new Intl.NumberFormat('pl-PL');

function Tile({
  icon: Icon,
  label,
  value,
  hint,
  tone,
  to,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint?: string;
  /**
   * ⚠️⚠️ `success` DOSZŁO NA ŻĄDANIE (01.09.2026): „skoro automaty na czerwono to błąd, to dajmy
   * na zielono, jak wszystko jest ok. Tak, żeby się odznaczało od reszty". Kafel bez tonu wygląda
   * jak każdy inny licznik — a pytanie, z którym się tu wchodzi, brzmi „czy jest dobrze".
   */
  tone?: 'success' | 'warning' | 'destructive';
  /** Zakładka, do której ten kafel prowadzi; bez niej kafel jest tylko liczbą. */
  to?: string;
}) {
  const tresc = (
    <CardContent className="flex items-start gap-3 pt-5">
      <div
        className={cn(
          'rounded-lg p-2',
          tone === 'destructive'
            ? 'bg-destructive/10 text-destructive'
            : tone === 'warning'
              ? 'bg-[var(--warning)]/10 text-[var(--warning)]'
              : tone === 'success'
                ? 'bg-[var(--success)]/10 text-[var(--success)]'
                : 'bg-primary/10 text-primary',
        )}
      >
        <Icon className="size-4" />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-semibold leading-tight tracking-tight">{value}</div>
        <div className="text-sm text-muted-foreground">{label}</div>
        {hint && <div className="mt-0.5 text-xs text-muted-foreground/80">{hint}</div>}
      </div>
    </CardContent>
  );

  /**
   * ⚠️ CAŁY KAFEL JEST ODNOŚNIKIEM, nie mała strzałka w kącie — zgłoszone 01.09.2026: „zróbmy też,
   * by każdy kafelek przenosił do odpowiadającej mu zakładki". Liczba, w którą nie da się kliknąć,
   * kończy się szukaniem tej samej rzeczy w nawigacji.
   *
   * ⚠️ KAFEL BEZ `to` ZOSTAJE ZWYKŁĄ KARTĄ — nie udajemy odnośnika tam, gdzie nie ma gdzie iść.
   */
  if (to === undefined) return <Card>{tresc}</Card>;
  return (
    <Card className="transition-colors hover:border-primary/40 hover:bg-accent/40">
      <Link to={to} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {tresc}
      </Link>
    </Card>
  );
}

/**
 * Wiersz stanu instancji.
 *
 * ⚠️ `hint` NIE JEST OZDOBĄ. Ta lista mówi jednym słowem o rzeczach, których nazwa nic nie
 * tłumaczy („magazyn plików: nieskonfigurowany"), a czytelnik nie ma jak zgadnąć, czy patrzy
 * na awarię, czy na wybór wdrożeniowy. Zdanie pod spodem jest tańsze niż telefon z pytaniem.
 */
function StatusRow({
  label,
  value,
  ok,
  hint,
  neutralnyGdyBrak = false,
}: {
  label: string;
  value: string;
  ok: boolean;
  /**
   * ⚠️ TREŚĆ, NIE NAPIS — bo jedna z podpowiedzi zawiera ODNOŚNIK do ekranu, na który odsyła
   * (zgłoszone 02.09.2026: „samo «Konta i API» może robić od razu za link do tego miejsca").
   * Zdanie mówiące „wydaj token tam" i niedające tam przejść to dwa kliknięcia zamiast jednego.
   */
  hint?: ReactNode;
  /** `true` = brak tej rzeczy jest STANEM POPRAWNYM, więc nie oznaczamy go jak braku. */
  neutralnyGdyBrak?: boolean;
}) {
  return (
    <div className="border-b border-border py-2 last:border-0">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="flex items-center gap-1.5 text-sm font-medium">
          {ok ? (
            <CheckCircle2 className="size-4 text-[var(--success)]" />
          ) : (
            <CircleDashed className={`size-4 ${neutralnyGdyBrak ? 'text-muted-foreground/50' : 'text-muted-foreground'}`} />
          )}
          {value}
        </span>
      </div>
      {hint !== undefined && <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{hint}</p>}
    </div>
  );
}

/**
 * PULPIT BEZ ANI JEDNEJ WYSPY — świeża instancja, jeszcze przed pierwszym agentem.
 *
 * ⚠️ TO NIE JEST STAN BŁĘDU I NIE MOŻE TAK WYGLĄDAĆ. Wcześniej cały ekran kończył się jednym
 * zdaniem „Brak dostępnych wysp synchronizacji" — zdanie prawdziwe, ale nie mówiące ani DLACZEGO
 * ich nie ma, ani CO z tym zrobić. Wyspa nie jest czymś, co się tu zakłada: powstaje sama przy
 * pierwszej rejestracji agenta, więc jedyną sensowną treścią tego ekranu jest droga do agenta.
 *
 * ⚠️ PRZYCISK POKAZUJEMY TYLKO TEMU, KTO MA TAM WSTĘP. Ekran Agentów wymaga modułu `agents`,
 * roli admina i konta SDI (`canSee` — ta sama funkcja, która buduje menu i pilnuje trasy), więc
 * przycisk dla admina klienta prowadziłby wprost w odmowę. Dla niego jest zdanie, co robić.
 */
function BezWyspy() {
  const { t } = useI18n();
  const { user } = useAuth();
  const pozycjaAgentow = navItemFor('/agenci');
  const wolno = pozycjaAgentow !== undefined && canSee(user, pozycjaAgentow);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('dashboard.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RadioTower className="size-4 text-primary" />
            {t('dashboard.noIslandTitle')}
          </CardTitle>
          <CardDescription className="max-w-2xl leading-relaxed">{t('dashboard.noIslandBody')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {wolno ? (
            <>
              <p className="max-w-2xl text-sm text-muted-foreground">{t('dashboard.noIslandSteps')}</p>
              <Link to="/agenci">
                <Button size="sm">
                  <RadioTower className="size-4" />
                  {t('dashboard.noIslandCta')}
                </Button>
              </Link>
            </>
          ) : (
            <p className="max-w-2xl text-sm text-muted-foreground">{t('dashboard.noIslandNoAccess')}</p>
          )}
        </CardContent>
      </Card>

      <KartaRuntime />
    </div>
  );
}

export function Dashboard() {
  const { t } = useI18n();
  const { groupId } = useIslands();
  // groupId JEST czescia klucza — inaczej po przelaczeniu wyspy cache podalby dane poprzedniej
  const ov = useQuery({
    queryKey: ['overview', groupId],
    queryFn: () => fetchOverview(groupId!),
    enabled: Boolean(groupId),
  });

  /**
   * Sygnały liczone przez portal z read-modelu. Pulpit bierze z nich JEDEN — rozjechany zegar —
   * bo to awaria bez objawu przy maszynie; resztę pokazuje ekran Alerty.
   */
  const wykryte = useQuery({
    queryKey: ['alerts', 'detected', groupId],
    queryFn: () => fetchDetected(groupId!),
    enabled: Boolean(groupId),
  });

  const overview = ov.data;
  const totals = overview?.island.totals;

  if (!groupId) return <BezWyspy />;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/**
        * SAM TYTUŁ, BEZ PODPISU POD NIM — na prośbę użytkownika (2026-08-18).
        *
        * ⚠️ USUNIĘTE ŚWIADOMIE, WIĘC ZAPISUJĘ CZEGO ZABRAKŁO: stał tu zakres kafli („dotyczą wyspy
        * X — Y"), dopisany kiedyś dlatego, że liczby na kaflach obejmują JEDNĄ wyspę, a nie całą
        * instancję. Wyspę widać teraz w przełączniku u góry ekranu, a przy jednej wyspie na
        * instancję — czyli u niemal każdego klienta — było to zdanie powtarzające nagłówek.
        * Gdyby kafle miały kiedyś liczyć coś innego niż wybraną wyspę, ta informacja musi wrócić.
        */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('dashboard.title')}</h1>
      </div>

      {/**
        * ROZJECHANY ZEGAR AUTOMATU — na pulpicie, bo ta awaria NIE MA OBJAWU przy maszynie.
        *
        * Automat działa, agent melduje, wydania przychodzą — tylko z niewłaściwą godziną, bo
        * `TRANSTARTDATETIME` w SPS jest odczytem zegara maszyny. Skutek widać dopiero w raporcie:
        * wydanie wpada do złego okresu rozliczeniowego, a limit z oknem kroczącym liczy się od
        * przesuniętej granicy. Nikt tego nie zgłosi, więc portal musi powiedzieć sam.
        *
        * Pasek pojawia się TYLKO przy niezerowej liczbie: stały „0 rozjechanych zegarów" po
        * tygodniu przestaje być czytany, a wtedy nie zostanie zauważony w dniu, w którym
        * przestanie pokazywać zero.
        */}
      {(wykryte.data?.clockSkew.count ?? 0) > 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-4 text-sm">
          <Clock className="mt-0.5 size-4 shrink-0" />
          <span>
            <span className="font-medium">
              {t('overview.clockSkewTitle')
                .replace('{n}', String(wykryte.data!.clockSkew.count))
                .replace('{outOf}', String(wykryte.data!.clockSkew.outOf))
                .replace('{min}', String(Math.round((wykryte.data!.thresholds.clockSkewSeconds ?? 300) / 60)))}
            </span>{' '}
            {t('overview.clockSkewWhy')}
            <span className="mt-1 block text-xs text-muted-foreground">
              {wykryte.data!.clockSkew.rows
                .slice(0, 4)
                .map((m) => `VMID ${m.vmid} ${m.name} — ${m.skewSeconds > 0 ? '+' : '−'}${m.minutes} min`)
                .join(' · ')}
            </span>
          </span>
        </div>
      )}

      {ov.isPending && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
      {ov.error && (
        <p className="text-sm text-destructive">
          {t('common.error')}: {(ov.error as Error).message}
        </p>
      )}

      {totals && overview && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/**
             * ⚠️ „Online" znaczy TO SAMO, co zielone na liście automatów: agent odezwał się
             * w oknie progu. Wcześniej ten kafel liczył kolumnę `online`, której nic nie
             * wypełnia (w SPS nie ma takiego pola), więc na prawdziwych danych pokazywał
             * zawsze zero — przy automacie, który na sąsiedniej zakładce świecił na zielono.
             *
             * Automaty BEZ AGENTA dopisujemy osobno, bo „0 online z 2" i „1 online, 1 bez
             * agenta" to dwie różne sytuacje: pierwsza jest awarią, druga niedokończonym
             * wdrożeniem.
             */}
            {/*
              ⚠️⚠️ KAFEL MÓWI TERAZ O ZDROWIU AUTOMATU, NIE TYLKO O NASZYM KANALE. Zgłoszone
              z testówki PESA (01.09.2026): „w sumie to wszystko leży, a ikonka Automaty na pulpicie
              nie ma żadnego wykrzyknika ani nic" — na trzech maszynach SPSUI nie działało, jedna
              milczała od poprzedniego dnia, a kafel pisał spokojnie „3 Automaty, 2 online".

              ⚠️ TON I POWODY LICZY SERWER (`stan-automatow.ts`), żeby ta ocena zgadzała się z listą
              automatów i z alertami. Ekran ich nie interpretuje — pokazuje pierwszy powód, bo po nim
              człowiek wie, czy wsiadać w samochód.
            */}
            <Tile
              icon={Boxes}
              label={t('overview.machines')}
              value={nf.format(totals.machines)}
              to="/automaty"
              /**
               * ⚠️ ZIELONY, GDY NIE MA SIĘ NA CO SKARŻYĆ — a nie „brak koloru". Serwer oddaje `ton`
               * tylko dla kłopotów, więc zdrowie zamieniamy tu na `success`: to jedyne miejsce,
               * które ma powiedzieć „sprawdzone, jest dobrze".
               */
              tone={totals.zdrowieAutomatow?.ton ?? (totals.machines > 0 ? 'success' : undefined)}
              hint={[
                `${totals.machinesOnline} ${t('overview.online')}`,
                ...(totals.zdrowieAutomatow?.powody ?? []),
                ...(totals.zdrowieAutomatow === undefined && totals.machinesNoAgent
                  ? [`${totals.machinesNoAgent} ${t('overview.noAgent')}`]
                  : []),
              ].join(' · ')}
            />
            <Tile icon={Users} label={t('overview.employees')} value={nf.format(totals.employees)} to="/pracownicy" />
            <Tile icon={Package} label={t('overview.items')} value={nf.format(totals.items)} to="/produkty" />
            <Tile
              icon={Receipt}
              label={t('overview.transactions')}
              value={nf.format(totals.transactions)}
              hint={`${nf.format(totals.transactions7d)} ${t('overview.last7d')}`}
              to="/transakcje"
            />
            {/*
              ⚠️⚠️ TRZY STANY, DOKŁADNIE JAK POPROSZONO (01.09.2026): „jak nie ma żadnego to na
              zielono, jak są żółte alerty to na żółto, a jak jest jakiś critical to na czerwono".
              Do tej pory kafel świecił na żółto ZAWSZE — także przy zerze — czyli kolor nie znaczył
              nic i nie dawał się odróżnić od tła.
            */}
            <Tile
              icon={AlertTriangle}
              label={t('overview.alertsOpen')}
              value={nf.format(totals.alertsOpen)}
              hint={`${totals.alertsCritical} ${t('overview.critical')}`}
              tone={
                totals.alertsCritical > 0 ? 'destructive' : totals.alertsOpen > 0 ? 'warning' : 'success'
              }
              to="/alerty"
            />
            {/*
              ⚠️ PRZEGRÓDKI NA WYCZERPANIU: zero to dobra wiadomość, więc zielone. Żółte dopiero, gdy
              jest co uzupełnić — inaczej ten kafel świeciłby ostrzeżeniem także na pełnym magazynie.
            */}
            <Tile
              icon={TrendingDown}
              label={t('overview.lowStock')}
              value={nf.format(totals.lowStock)}
              tone={totals.lowStock > 0 ? 'warning' : 'success'}
              to="/planogram"
            />
            {/*
              ⚠️ ZMIANY W KOLEJCE: pusta kolejka znaczy „wszystko zeszło do maszyn" — zielone.
              Niepusta nie jest awarią (zmiany dojeżdżają cyklami), więc żółte, a nie czerwone.
            */}
            <Tile
              icon={Clock}
              label={t('overview.changesQueued')}
              value={nf.format(totals.changesQueued)}
              hint={`${nf.format(totals.changesConfirmed)} ${t('overview.confirmed')}`}
              tone={totals.changesQueued > 0 ? 'warning' : 'success'}
              to="/zmiany"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('overview.islands')}</CardTitle>
              {/**
                * ⚠️ JEDNO ZDANIE NAD TABELĄ, BO O TO BYŁO ZGŁOSZENIE. Przy sześciu wyspach nikt nie
                * przegląda sześciu wierszy codziennie — a pytanie jest jedno: „czy gdzieś jest
                * problem". Zdanie odpowiada na nie bez czytania tabeli; tabela mówi GDZIE.
                */}
              <PodsumowanieFloty grupy={overview.groups} />
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[36rem] text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">
                        {t('overview.client')}
                      </th>
                      <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">
                        {t('overview.island')}
                      </th>
                      {/**
                        * ⚠️ DYMEK PRZY „TRYB CDC", bo sama nazwa MYLI — i zmyliła (26.08.2026,
                        * pytanie „co to jest tryb CDC?"). To pole opisuje, co ma BAZA KLIENTA,
                        * a NIE z czego korzysta nasza synchronizacja: agent nigdy go nie czyta
                        * i czyta tabele własnym kursorem. Znana luka modelu: enum `CdcMode` nie
                        * ma wartości „nie używamy CDC" (patrz komentarz w `prisma/seed.ts`).
                        */}
                      <th
                        scope="col"
                        className="px-3 py-2 text-left font-medium text-muted-foreground"
                        title={t('overview.cdcHint')}
                      >
                        {t('overview.cdc')}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right font-medium text-muted-foreground">
                        {t('overview.machines')}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right font-medium text-muted-foreground">
                        {t('overview.employees')}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right font-medium text-muted-foreground">
                        {t('overview.transactions')}
                      </th>
                      <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">
                        {t('overview.health')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.groups.map((g, i) => (
                      <tr
                        key={g.id}
                        className={cn(
                          'border-b border-border/60 last:border-0',
                          i % 2 === 1 && 'bg-muted/25',
                          // wybrana wyspa podswietlona, zeby bylo widac, ktorej dotycza kafle wyzej
                          g.id === groupId && 'bg-primary/10',
                        )}
                      >
                        <td className="px-3 py-2">{g.client}</td>
                        <td className="px-3 py-2 font-medium">
                          {g.name}
                          {g.id === groupId && (
                            <Badge variant="default" className="ml-2">
                              {t('overview.selected')}
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="neutral">{g.cdcMode === 'NATIVE_CT' ? 'Change Tracking' : 'dziennik'}</Badge>
                        </td>
                        <td className="px-3 py-2 text-right">{nf.format(g.machines)}</td>
                        <td className="px-3 py-2 text-right">{nf.format(g.employees)}</td>
                        <td className="px-3 py-2 text-right">{nf.format(g.transactions)}</td>
                        <td className="px-3 py-2">
                          <StanWyspyKomorka
                            zdrowie={g.zdrowie ?? null}
                            groupId={g.id}
                            nieudane={g.nieudaneOperacje}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <KartaRuntime sync={overview?.island.synchronizacja} />
    </div>
  );
}

type Zdrowie = NonNullable<Overview['groups'][number]['zdrowie']>;

/**
 * PODSUMOWANIE FLOTY — jedno zdanie nad tabelą wysp.
 *
 * ⚠️ ISTNIEJE, BO O TO BYŁO ZGŁOSZENIE (2026-08-19). U klienta jest sześć wysp; sprawdzenie, czy
 * na którejś nie stoi agent, wymagało przeskakiwania przełącznikiem przez wszystkie sześć, bo każdy
 * ekran pokazuje tylko WYBRANĄ. Nikt tego nie robi codziennie — i tak się skończyło tym, że problem
 * z agentem stał niezauważony przez długi czas. Zdanie odpowiada na pytanie „czy gdzieś jest
 * problem", tabela mówi GDZIE.
 */
function PodsumowanieFloty({ grupy }: { grupy: Overview['groups'] }) {
  const { t } = useI18n();
  const ze = grupy.filter((g) => g.zdrowie != null);
  /** Instancja starsza od tej zmiany nie oddaje stanu — wtedy nie ma o czym podsumowywać. */
  if (ze.length === 0) return null;

  const awarie = ze.filter((g) => g.zdrowie!.stan === 'AWARIA');
  const uwagi = ze.filter((g) => g.zdrowie!.stan === 'UWAGA');

  if (awarie.length === 0 && uwagi.length === 0) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-[var(--success)]">
        <ShieldCheck className="size-4 shrink-0" aria-hidden="true" />
        {t('overview.healthAllOk').replace('{ile}', String(ze.length))}
      </p>
    );
  }

  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      {awarie.length > 0 && (
        <span className="flex items-center gap-1.5 font-medium text-[var(--destructive)]">
          <XCircle className="size-4 shrink-0" aria-hidden="true" />
          {t('overview.healthDown').replace('{ile}', String(awarie.length)).replace('{wyspy}', awarie.map((g) => g.name).join(', '))}
        </span>
      )}
      {uwagi.length > 0 && (
        <span className="flex items-center gap-1.5 text-[var(--warning)]">
          <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
          {t('overview.healthWarn').replace('{ile}', String(uwagi.length)).replace('{wyspy}', uwagi.map((g) => g.name).join(', '))}
        </span>
      )}
    </p>
  );
}

/**
 * STAN JEDNEJ WYSPY W WIERSZU: znak, liczba odpowiadających agentów i POWÓD.
 *
 * ⚠️ POWÓD JEST TU WAŻNIEJSZY OD ZNAKU. Sam kolorowy znaczek mówi „coś jest nie tak" i zostawia
 * człowieka z sześcioma wyspami do sprawdzenia — czyli z tym, co miało zniknąć. Zdanie „żaden agent
 * nie odpowiada (ostatni kontakt 3 h 20 min)" mówi od razu, gdzie iść i czego szukać.
 */
function StanWyspyKomorka({
  zdrowie,
  groupId,
  nieudane,
}: {
  zdrowie: Zdrowie | null;
  groupId: string;
  nieudane: number | undefined;
}) {
  const { t } = useI18n();
  if (zdrowie == null) return <span className="text-xs text-muted-foreground">—</span>;

  const { stan, powody, agenci } = zdrowie;
  const bezKlucza = zdrowie.bezKlucza ?? null;
  const ikona =
    stan === 'AWARIA' ? (
      <XCircle className="size-4 shrink-0 text-[var(--destructive)]" aria-hidden="true" />
    ) : stan === 'UWAGA' ? (
      <AlertTriangle className="size-4 shrink-0 text-[var(--warning)]" aria-hidden="true" />
    ) : (
      <ShieldCheck className="size-4 shrink-0 text-[var(--success)]" aria-hidden="true" />
    );

  return (
    <div className="flex items-start gap-1.5">
      {ikona}
      <div className="min-w-0">
        <p className="text-xs font-medium">
          {stan === 'OK' ? t('overview.healthOk') : stan === 'UWAGA' ? t('overview.healthAttention') : t('overview.healthFail')}
          <span className="ml-2 font-normal text-muted-foreground">
            {/*
              ⚠️ „ODPOWIADA / POTWIERDZONYCH", nie „online". Węzeł czekający na potwierdzenie melduje
              się co minutę i po samym kontakcie wygląda zdrowo, a danych nie wysyła — liczba
              potwierdzonych jest tym, co decyduje, czy z wyspy cokolwiek płynie.
            */}
            {t('overview.healthAgents')
              .replace('{odpowiada}', String(agenci.odpowiada))
              .replace('{wszystkich}', String(agenci.potwierdzone))}
            {agenci.czeka > 0 ? ` · ${t('overview.healthWaiting').replace('{ile}', String(agenci.czeka))}` : ''}
          </span>
        </p>
        {powody.length > 0 && (
          <ul className="mt-0.5 space-y-0.5">
            {powody.map((p) => (
              <li key={p} className="text-[11px] leading-snug text-muted-foreground">
                {p}
              </li>
            ))}
          </ul>
        )}
        {/*
          ⚠️⚠️ ODŚRODEK DO NIEUDANYCH OPERACJI — ODPOWIEDŹ NA „NIE WIEM GDZIE TO SPRAWDZIĆ"
          (zgłoszenie z produkcji PESA, 04.09.2026). Pulpit mówił „37 nieudanych operacji" i nie
          prowadził NIGDZIE; operacje były widoczne na ekranie Zmian, ale nic o tym nie mówiło.
          Powiadomienie bez drogi do przyczyny jest połową wiadomości.

          ⚠️ LINK BUDUJEMY Z LICZBY, NIE Z TEKSTU POWODU. Powody są napisami dla człowieka —
          wyłuskiwanie z nich liczby przez wzorzec padłoby przy pierwszej zmianie brzmienia albo
          tłumaczeniu. Osobne pole `nieudaneOperacje` jest tu całym powodem, dla którego API je oddaje.

          ⚠️ `undefined` (starsza instancja) NIE POKAZUJE NICZEGO — nie ma jak sprawdzić, czy tam
          jest co otwierać, a link do pustego ekranu jest gorszy od jego braku.
        */}
        {nieudane !== undefined && nieudane > 0 && (
          <Link
            /**
             * ⚠️⚠️ `/zmiany`, NIE `/changes` — POPRAWKA PO ZGŁOSZENIU: „po wejściu w link jest
             * pusta strona". Trasa w `App.tsx` nazywa się po polsku, a ja wpisałem tu angielską
             * nazwę modułu. Router nie ma trasy zapasowej, więc nieistniejąca ścieżka nie daje
             * błędu — daje BIAŁĄ STRONĘ, czyli objaw, który nie mówi nic o przyczynie.
             *
             * ⚠️ `groupId` W ADRESIE MA ZNACZENIE: ekran zmian bierze wyspę z globalnego wyboru,
             * więc bez tego parametru link z wiersza jednej wyspy otwierałby zmiany INNEJ — tej
             * aktualnie wybranej. `Changes.tsx` przyjmuje go i przestawia wybór.
             */
            to={`/zmiany?groupId=${groupId}&status=FAILED`}
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[var(--primary)] hover:underline"
          >
            <ListChecks className="size-3" aria-hidden="true" />
            {t('overview.healthShowFailed').replace('{ile}', String(nieudane))}
          </Link>
        )}
        {/*
          ═══════════════════════════════════════════════════════════
           ODNOŚNIK DO TABEL BEZ KLUCZA GŁÓWNEGO — z powodem i podpowiedzią naprawy.

           ⚠️⚠️ POWSTAŁO Z POMIARU NA PRODUKCJI TELEPLAN (04.09.2026). Agent meldował „pominięto
           Drawers: brak klucza głównego" OD STYCZNIA, portal to zapisywał i rysował jako jedną
           z uwag na ekranie Agentów — a nigdzie nie stało, że skutkiem jest BRAK PLANOGRAMU tej
           wyspy. Pomiar był, znaczenia nie było.

           ⚠️ LINK Z LICZBY TABEL, NIE Z TEKSTU POWODU — ta sama zasada co przy nieudanych
           operacjach obok: powody są napisami dla człowieka i wyłuskiwanie z nich czegokolwiek
           wzorcem padłoby przy pierwszej zmianie brzmienia.
          ═══════════════════════════════════════════════════════════
        */}
        {bezKlucza !== null && bezKlucza.tabele.length > 0 && (
          <Link
            /**
             * ⚠️ BEZ `groupId` I TO JEST SPRAWDZONE, NIE PRZEOCZONE: ekran Agentów pokazuje CAŁĄ
             * instancję (`listAgents()` bez wyspy), więc parametr wyspy byłby tam martwy — a martwy
             * parametr w adresie to obietnica filtru, którego nie ma. Karta na tamtym ekranie
             * nazywa wyspę i węzeł, więc wiadomo, o którą chodzi.
             */
            to="/agenci"
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[var(--warning)] hover:underline"
          >
            <KeyRound className="size-3" aria-hidden="true" />
            {t('overview.healthNoPk').replace('{ile}', String(bezKlucza.tabele.length))}
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * ZDANIE POD WIERSZEM „SILNIK SYNCHRONIZACJI" — czyli odpowiedź na „czy master poprawnie chodzi".
 *
 * ⚠️ CZTERY RÓŻNE STANY, KTÓRE WYMAGAJĄ RÓŻNYCH REAKCJI, i dlatego nie da się ich zwinąć w jedno
 * słowo w kolumnie obok:
 *   • wyspa bez węzła z bazą — nie ma jeszcze czego włączać (zadanie wdrożeniowe),
 *   • master nieustalony — synchronizacja może chodzić, ale portal nie wie, GDZIE stoi master,
 *   • master milczy albo melduje problem — awaria z konkretnym powodem,
 *   • master odpowiada — jedyny stan, w którym nie ma o czym mówić poza „kiedy ostatnio".
 *
 * ⚠️ `undefined` (instancja starsza od tej zmiany) NIE JEST BŁĘDEM — wtedy wiersz mówi tylko
 * o module, a nie o wyspie, i podpis to przyznaje wprost.
 */
function podpisSynchronizacji(
  t: (k: string) => string,
  sync: Overview['island']['synchronizacja'],
): string | undefined {
  if (sync == null) return t('dashboard.syncModul');
  if (sync.wezlowZBaza === 0) return t('dashboard.syncBrakWezlow');

  const baza = sync.masterBaza ?? '—';

  /**
   * ⚠️⚠️ ZWYKŁA WYSPA NIE MA ŻADNEGO WĘZŁA Z MASTEREM I TO JEST POPRAWNY STAN (zgłoszone
   * 02.09.2026). Wcześniej ten sam brak czytał się jako „portal nie wie, gdzie stoi master",
   * więc pulpit odsyłał do zasiania mastera — operacji, która na takiej wyspie nie ma sensu
   * i której nikt nie powinien tam wykonywać. Uzasadnienie: `api/src/overview/synchronizacja.ts`.
   *
   * ⚠️ BRAK POLA (instancja starsza od tej zmiany) NIE UDAJE WIEDZY: zostaje sama nazwa bazy,
   * bez zdania o tym, gdzie stoi.
   */
  if (sync.gdzieMaster === 'MASZYNA') {
    return t(sync.masterPotwierdzony ? 'dashboard.syncMasterNaMaszynie' : 'dashboard.syncMasterDoPotwierdzenia').replace(
      '{baza}',
      baza,
    );
  }
  if (sync.gdzieMaster === 'NIEZASIANY') return t('dashboard.syncMasterNieustalony').replace('{baza}', baza);
  if (sync.gdzieMaster === undefined && sync.masterWezel === null) {
    return t('dashboard.syncMasterSamaBaza').replace('{baza}', baza);
  }

  const minut = sync.masterOdKontaktuMin === null ? '—' : String(sync.masterOdKontaktuMin);
  const podstawa = (klucz: string) =>
    t(klucz).replace('{baza}', baza).replace('{wezel}', sync.masterWezel ?? '—').replace('{minut}', minut);

  if (sync.masterOk === true) return podstawa('dashboard.syncMasterOk');
  /** Powód doklejamy, gdy jest — „nie odpowiada" bez powodu kazałoby szukać po omacku. */
  return `${podstawa('dashboard.syncMasterZly')}${sync.masterProblem === null ? '' : ` ${sync.masterProblem}`}`;
}

/**
 * PODPIS POD POZYCJĄ „TOKEN VENDING+" — mówi, CO ZROBIĆ, a nie tylko jak jest.
 *
 * ⚠️⚠️ „WYDANY, ALE NIGDY NIE UŻYTY" TO NAJCIEKAWSZA ODPOWIEDŹ, jaką ta pozycja daje: sekret
 * powstał u nas i NIE DOJECHAŁ do drugiej strony (albo ich integracja nie wystartowała). Bez tego
 * zdania zielona ptaszka kazałaby szukać usterki wszędzie indziej.
 */
function podpisVendingPlus(t: (k: string) => string, s: StanVendingPlus): ReactNode {
  if (!s.wydany) {
    /**
     * ⚠️⚠️ ODSYŁAMY DO ISTNIEJĄCEGO EKRANU I ROBIMY Z NIEGO ODNOŚNIK (zgłoszone 02.09.2026).
     * Zdanie wskazywało „Ustawienia → Tokeny panelu" — takiego miejsca w portalu NIE MA:
     * tokeny wydaje się w „Administracja → Konta i API". Błędne wskazanie jest gorsze niż brak
     * wskazania: człowiek szuka zakładki, której nie znajdzie, i wnioskuje, że nie ma uprawnień.
     *
     * ⚠️ TEKST DZIELIMY NA PLACEHOLDERZE, a nie składamy z dwóch kluczy — tłumaczenie musi
     * zostać jednym zdaniem, żeby dało się je przetłumaczyć z zachowaniem szyku.
     */
    const [przed, po] = t('dashboard.vendingPlusBrakHint').split('{gdzie}');
    return (
      <>
        {przed}
        <Link to="/konta" className="text-primary underline decoration-dotted">
          {t('nav.accounts')}
        </Link>
        {po}
      </>
    );
  }
  const kiedy = s.wydanoAt === null ? '' : new Date(s.wydanoAt).toLocaleDateString('pl-PL');
  if (s.ostatnioUzyty === null) return t('dashboard.vendingPlusNieuzywany').replace('{kiedy}', kiedy);
  const podstawa = t('dashboard.vendingPlusHint')
    .replace('{kiedy}', kiedy)
    .replace('{ostatnio}', new Date(s.ostatnioUzyty).toLocaleString('pl-PL'))
    .replace('{n}', s.uzyc.toLocaleString('pl-PL'));
  return s.pelnyZakres ? podstawa : `${podstawa} ${t('dashboard.vendingPlusWaskiHint')}`;
}

/**
 * KONFIGURACJA RUNTIME — świadomie POZA warunkiem „czy jest wyspa".
 *
 * ⚠️ WCZEŚNIEJ ZNIKAŁA RAZEM Z CAŁYM PULPITEM. Brak wyspy kończył render jednym zdaniem, więc
 * świeża instancja nie pokazywała ANI wersji, ANI trybu logowania, ANI magazynu plików — czyli
 * dokładnie tych trzech rzeczy, które sprawdza się PRZED wdrożeniem pierwszego agenta. Ta karta
 * nie zależy od wyspy (czyta `GET /api/config`), więc nie ma powodu, żeby jej brak ukrywał.
 */
function KartaRuntime({ sync }: { sync?: Overview['island']['synchronizacja'] }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const cfg = useQuery({ queryKey: ['runtime-config'], queryFn: fetchRuntimeConfig });
  /**
   * ⚠️⚠️ TOKEN VENDING+ NA PULPICIE — zgłoszone (31.08.2026): „czy jest wydany token do vending+,
   * bo to ma działać na każdej instancji". Brak tokenu znaczy, że CAŁA integracja klienta milczy,
   * i milczy bez żadnego błędu po naszej stronie — a dotąd trzeba było wejść na osobny ekran.
   *
   * ⚠️ PYTAMY TYLKO NASZYMI KONTAMI (`clientId === null` = SDI). Trasa ma bramkę admin + SDI, więc
   * bez tego warunku konto klienta dostawałoby 403 przy każdym wejściu na pulpit — a i tak nie ma
   * co zrobić z odpowiedzią „brak tokenu": wydanie go jest naszą robotą przy wdrożeniu instancji.
   */
  const naszeKonto = user !== null && user.clientId === null;
  const vendingPlus = useQuery({
    queryKey: ['stan-vending-plus'],
    queryFn: fetchStanVendingPlus,
    enabled: naszeKonto,
  });

  return (
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.runtime')}</CardTitle>
          {/*
            ⚠️ DWA ŹRÓDŁA, WIĘC PISZEMY DWA. Wiersz „Silnik synchronizacji" przestał czytać flagę
            z `/api/config` (była zaszyta na `false` od czasów v1) i bierze stan z przeglądu wyspy —
            a karta, która podaje swoje źródło, nie może wymieniać połowy.
          */}
          <CardDescription>GET /api/config{sync == null ? '' : ' + /api/overview'}</CardDescription>
        </CardHeader>
        <CardContent>
          {cfg.data && (
            <div>
              <StatusRow label={t('dashboard.version')} value={`v${cfg.data.app.version}`} ok />
              <StatusRow label={t('dashboard.auth')} value={cfg.data.auth.mode} ok={cfg.data.auth.mode !== 'unconfigured'} />
              {/**
               * ⚠️ BEZ PODPOWIEDZI, I TO JEST DECYZJA, NIE PRZEOCZENIE. Stały tu kolejno dwa
               * opisy: pierwszy zestarzał się i twierdził, że „dziś żadna funkcja z magazynu
               * nie korzysta" (korzystają dwie — obrazki towarów i kopie baz), drugi opisywał
               * je poprawnie i został uznany za zbędny. Sama pozycja mówi, czy magazyn jest
               * podłączony, a to jest wszystko, po co się na nią patrzy.
               */}
              <StatusRow
                label={t('dashboard.storage')}
                value={cfg.data.features.storage ? t('common.configured') : t('dashboard.storageOff')}
                ok={cfg.data.features.storage}
                /** Brak magazynu NIE jest błędem, więc nie świeci na czerwono. */
                neutralnyGdyBrak
              />
              {/*
                ⚠️⚠️ TEN WIERSZ CZYTAŁ MARTWĄ FLAGĘ I MIESIĄCAMI KŁAMAŁ. Brał `features.sync`
                z `GET /api/config`, gdzie stało zaszyte `false` z czasów v1 („brak agentów/sync") —
                więc pisał „wyłączony", podczas gdy trzy maszyny klienta i chmurowy master
                synchronizowały bez przerwy. Zgłoszone przez użytkownika 25.08.2026, przy okazji
                zasiania mastera w chmurze.

                ⚠️ FLAGA NIE MOGŁA BYĆ PRAWDZIWA: `/api/config` jest PUBLICZNE (front musi znać tryb
                logowania przed zalogowaniem), więc nie ma prawa mówić o wyspach. Stan liczy teraz
                `island.synchronizacja` z przeglądu wyspy; flaga została tam, gdzie jest jej miejsce —
                mówi „instancja ma ten moduł" i służy jako zapas dla instancji starszej od zmiany.
              */}
              {/*
                ⚠️ POZYCJA STOI NAD SILNIKIEM SYNCHRONIZACJI, bo o tej kolejności był wprost
                zgłoszony wymóg — i ma sens: token jest warunkiem integracji Z ZEWNĄTRZ, a silnik
                mówi o tym, co dzieje się w środku wyspy.
              */}
              {naszeKonto && vendingPlus.data !== undefined && (
                <StatusRow
                  label={t('dashboard.vendingPlus')}
                  value={
                    vendingPlus.data.wydany
                      ? vendingPlus.data.pelnyZakres
                        ? t('dashboard.vendingPlusWydany')
                        : t('dashboard.vendingPlusWaski')
                      : t('dashboard.vendingPlusBrak')
                  }
                  /** ⚠️ ZIELONO TYLKO PRZY PEŁNYM ZAKRESIE — węższy token przechodzi logowanie i odbija się od części tras. */
                  ok={vendingPlus.data.wydany && vendingPlus.data.pelnyZakres}
                  hint={podpisVendingPlus(t, vendingPlus.data)}
                />
              )}
              {/*
                ⚠️ TRYB WYSPY STOI W WARTOŚCI, NIE W ZDANIU POD SPODEM (zgłoszone 02.09.2026:
                „powinien wykrywać ten tryb z ustawienia instancji i po prostu wyświetlać
                w kafelku «zwykły»/«Smart Sync»"). Wcześniej podpis kończył się wtrętem „tak jak
                w trybie zwykłym", czyli opisywał tryb OKÓLNIKIEM, zamiast go nazwać — a od trybu
                zależy, czy różnica bazy maszyny wobec mastera jest usterką, czy stanem normalnym.
              */}
              <StatusRow
                label={t('dashboard.sync')}
                value={[
                  sync == null
                    ? cfg.data.features.sync
                      ? t('common.enabled')
                      : t('common.disabled')
                    : sync.czynna
                      ? t('common.enabled')
                      : t('common.disabled'),
                  /** Brak pola = instancja starsza od tej zmiany; wtedy nie dopisujemy nic. */
                  sync?.tryb === undefined
                    ? null
                    : t(sync.tryb === 'SMART_SYNC' ? 'dashboard.trybSmartSync' : 'dashboard.trybZwykly'),
                ]
                  .filter((x): x is string => x !== null)
                  .join(' · ')}
                ok={sync == null ? cfg.data.features.sync : sync.czynna}
                hint={podpisSynchronizacji(t, sync)}
              />
            </div>
          )}
        </CardContent>
      </Card>
  );
}
